import React, { useState, useCallback } from 'react';
import { Plus, FileText, Trash2, Edit3, Save, X } from 'lucide-react';
import { BiographyContentProps, BiographyField as BiographyFieldType } from './types';
import BiographyField from './BiographyField';
import BiographyCard from './BiographyCard';
import Button from '../Common/Button';
import Input from '../Common/Input';
import './BiographySystem.css';

interface DynamicContentProps extends BiographyContentProps {
  onAddField: (field: Omit<BiographyFieldType, 'id'>) => void;
  onRemoveField: (fieldId: string) => void;
  allowCustomFields?: boolean;
  maxFields?: number;
  fieldTemplate?: Partial<BiographyFieldType>;
  emptyMessage?: string;
  addButtonText?: string;
  useCard?: boolean;
}

/**
 * DynamicContent - Handles dynamic add/remove of biography fields
 * Used for journal entries, custom fields, and expandable content
 */
const DynamicContent: React.FC<DynamicContentProps> = ({
  fields,
  onFieldChange,
  onAddField,
  onRemoveField,
  allowCustomFields = true,
  maxFields = 50,
  fieldTemplate = { type: 'textarea', editable: true },
  showLabels = true,
  emptyMessage = 'No entries yet. Click "Add Entry" to get started.',
  addButtonText = 'Add Entry',
  useCard = true
}) => {
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  // Handle adding a new field
  const handleAddField = useCallback(() => {
    if (!newFieldLabel.trim()) return;

    const newField: Omit<BiographyFieldType, 'id'> = {
      label: newFieldLabel.trim(),
      value: fieldTemplate.type === 'list' ? [] : newFieldValue,
      type: fieldTemplate.type || 'textarea',
      editable: fieldTemplate.editable ?? true,
      placeholder: fieldTemplate.placeholder,
      maxLength: fieldTemplate.maxLength,
      required: fieldTemplate.required ?? false
    };

    onAddField(newField);
    setNewFieldLabel('');
    setNewFieldValue('');
    setIsAddingField(false);
  }, [newFieldLabel, newFieldValue, fieldTemplate, onAddField]);

  // Handle canceling field addition
  const handleCancelAdd = useCallback(() => {
    setNewFieldLabel('');
    setNewFieldValue('');
    setIsAddingField(false);
  }, []);

  // Handle removing a field
  const handleRemoveField = useCallback((fieldId: string) => {
    onRemoveField(fieldId);
    if (editingFieldId === fieldId) {
      setEditingFieldId(null);
    }
  }, [onRemoveField, editingFieldId]);

  // Toggle edit mode for a specific field
  const toggleFieldEdit = useCallback((fieldId: string) => {
    setEditingFieldId(editingFieldId === fieldId ? null : fieldId);
  }, [editingFieldId]);

  const canAddMore = fields.length < maxFields;

  // Helper to wrap content in card if needed
  const wrapInCard = (content: React.ReactNode, variant: 'default' | 'outlined' = 'default', padding: 'sm' | 'md' | 'lg' = 'md'): React.ReactElement => {
    if (!useCard) return <>{content}</>;
    return <BiographyCard variant={variant} padding={padding}>{content}</BiographyCard>;
  };

  if (fields.length === 0) {
    const emptyContent = (
      <>
        <div className="biography-empty">
          <div className="biography-empty__icon">
            <FileText size={32} />
          </div>
          <div className="biography-empty__message">
            {emptyMessage}
          </div>
        </div>

        {allowCustomFields && canAddMore && (
          <div className="biography-actions biography-actions--center">
            <Button
              variant="SECONDARY"
              size="SMALL"
              onClick={() => setIsAddingField(true)}
            >
              <Plus size={14} />
              {addButtonText}
            </Button>
          </div>
        )}

        {/* Add field form */}
        {isAddingField && wrapInCard(
          <>
            <div className="biography-fields">
              <div className="biography-field">
                <label className="biography-field__label">
                  Entry Title
                </label>
                <Input
                  value={newFieldLabel}
                  onChange={(value) => setNewFieldLabel(value)}
                  placeholder="Enter a title for this entry..."
                />
              </div>
              {fieldTemplate.type === 'textarea' && (
                <div className="biography-field">
                  <label className="biography-field__label">
                    Entry Content
                  </label>
                  <textarea
                    className="biography-field__input"
                    value={newFieldValue}
                    onChange={(e) => setNewFieldValue(e.target.value)}
                    placeholder={fieldTemplate.placeholder || 'Enter content...'}
                    maxLength={fieldTemplate.maxLength}
                    rows={6}
                  />
                  {fieldTemplate.maxLength && (
                    <div className="biography-field__help-text">
                      {newFieldValue.length} / {fieldTemplate.maxLength} characters
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="biography-actions biography-actions--right">
              <Button
                variant="CLEAR"
                size="SMALL"
                onClick={handleCancelAdd}
              >
                Cancel
              </Button>
              <Button
                variant="PRIMARY"
                size="SMALL"
                onClick={handleAddField}
                disabled={!newFieldLabel.trim()}
              >
                Add Entry
              </Button>
            </div>
          </>,
          'outlined',
          'sm'
        )}
      </>
    );

    return wrapInCard(emptyContent, 'default', 'lg');
  }

  const fieldsContent = (
    <>
      {/* Existing fields */}
      <div className="biography-fields">
        {fields.map(field => (
          <div key={field.id} className="biography-field-container">
            <BiographyField
              field={{
                ...field,
                editable: field.editable && editingFieldId === field.id
              }}
              value={field.value}
              onChange={(value) => onFieldChange(field.id, value)}
              showLabel={showLabels}
            />

            {/* Field actions */}
            <div className="biography-field-actions">
              {field.editable && (
                <Button
                  variant="CLEAR"
                  size="SMALL"
                  onClick={() => toggleFieldEdit(field.id)}
                >
                  {editingFieldId === field.id ? (
                    <>
                      <Save size={12} />
                      Done
                    </>
                  ) : (
                    <>
                      <Edit3 size={12} />
                      Edit
                    </>
                  )}
                </Button>
              )}

              <Button
                variant="CLEAR"
                size="SMALL"
                onClick={() => handleRemoveField(field.id)}
                className="biography-field-action--danger"
              >
                <Trash2 size={12} />
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new field */}
      {allowCustomFields && canAddMore && (
        <>
          {!isAddingField ? (
            <div className="biography-actions biography-actions--center">
              <Button
                variant="CLEAR"
                size="SMALL"
                onClick={() => setIsAddingField(true)}
              >
                <Plus size={14} />
                {addButtonText}
              </Button>
            </div>
          ) : wrapInCard(
            <>
              <div className="biography-fields">
                <div className="biography-field">
                  <label className="biography-field__label">
                    Entry Title
                  </label>
                  <Input
                    value={newFieldLabel}
                    onChange={(value) => setNewFieldLabel(value)}
                    placeholder="Enter a title for this entry..."
                  />
                </div>
                {fieldTemplate.type === 'textarea' && (
                  <div className="biography-field">
                    <label className="biography-field__label">
                      Entry Content
                    </label>
                    <textarea
                      className="biography-field__input"
                      value={newFieldValue}
                      onChange={(e) => setNewFieldValue(e.target.value)}
                      placeholder={fieldTemplate.placeholder || 'Enter content...'}
                      maxLength={fieldTemplate.maxLength}
                      rows={6}
                    />
                    {fieldTemplate.maxLength && (
                      <div className="biography-field__help-text">
                        {newFieldValue.length} / {fieldTemplate.maxLength} characters
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="biography-actions biography-actions--right">
                <Button
                  variant="CLEAR"
                  size="SMALL"
                  onClick={handleCancelAdd}
                >
                  <X size={14} />
                  Cancel
                </Button>
                <Button
                  variant="PRIMARY"
                  size="SMALL"
                  onClick={handleAddField}
                  disabled={!newFieldLabel.trim()}
                >
                  <Plus size={14} />
                  Add Entry
                </Button>
              </div>
            </>,
            'outlined',
            'sm'
          )}
        </>
      )}

      {/* Field limit message */}
      {!canAddMore && (
        <div className="biography-field__help-text">
          Maximum of {maxFields} entries reached.
        </div>
      )}
    </>
  );

  return wrapInCard(fieldsContent, 'default', 'md');
};

export default DynamicContent;